import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { History, FileDown, Loader2 } from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";
import { useI18n } from "../context/LanguageContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "./ui/tabs";
import { Button } from "./ui/button";
import { AiCompareTable } from "./AiCompareTable";
import { AiArchive } from "./AiArchive";
import { AI_FIELDS, toReviewItems, reviewToRows } from "../lib/aiReview";
import { parseNumber } from "../lib/clipboardGrid";

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString("lt-LT") : "");

export function AiHistoryDialog({ open, onOpenChange, projectId, rows, setRows, columns, setColumns, isEmptyRow, uid, fillEmptyRows, onOpenReview, onDownloadPdf }) {
    const { t } = useI18n();
    const [list, setList] = useState(null);
    const [cache, setCache] = useState({});
    const [cur, setCur] = useState(null);
    const err = (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail));
    const load = async (aid) => {
        if (cache[aid]) return cache[aid];
        const { data } = await api.get(`/projects/${projectId}/ai-analyses/${aid}`);
        setCache((c) => ({ ...c, [aid]: data }));
        return data;
    };
    useEffect(() => {
        if (!open) return;
        setCache({});
        setList(null);
        api.get(`/projects/${projectId}/ai-analyses`).then(({ data }) => { setList(data); setCur((c) => (data.some((a) => a.id === c) ? c : data[0]?.id || null)); }).catch((e) => { err(e); setList([]); });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, projectId]);
    useEffect(() => {
        if (open && cur) load(cur).catch(err);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, cur]);
    const analysis = cur ? cache[cur] : null;

    const downloadVersion = async () => {
        if (!analysis?.pdf_path) { onDownloadPdf(); return; }
        try {
            const { data } = await api.get(`/projects/${projectId}/ai-analyses/${analysis.id}/pdf`, { responseType: "blob" });
            const url = URL.createObjectURL(data);
            const a = document.createElement("a");
            a.href = url; a.download = analysis.pdf_filename || "original.pdf"; a.click();
            setTimeout(() => URL.revokeObjectURL(url), 2000);
        } catch (e) { err(e); }
    };

    const restoreAll = () => {
        if (!analysis || !window.confirm(t("ah_restore_q"))) return;
        const linked = new Set();
        setRows((p) => p.map((r) => {
            if (r.ai_src?.aid !== analysis.id) return r;
            const it = analysis.items[r.ai_src.idx];
            if (!it) return r;
            linked.add(r.ai_src.idx);
            const upd = Object.fromEntries(AI_FIELDS.filter((f) => r.ai_orig && f.target in r.ai_orig)
                .map((f) => [f.target, f.num ? parseNumber(it[f.key]?.value) : String(it[f.key]?.value ?? "")]));
            return { ...r, ...upd, edits: {} };
        }));
        const imported = new Set((analysis.imported || []).map((l) => l.idx));
        const missing = toReviewItems(analysis.items).filter((it) => imported.has(it._id) && !rows.some((r) => r.ai_src?.aid === analysis.id && r.ai_src.idx === it._id));
        if (missing.length) {
            const { rows: add, newCols } = reviewToRows(missing, AI_FIELDS.map((f) => f.key), uid, columns, analysis.id);
            if (newCols.length) setColumns((cs) => [...cs, ...newCols]);
            setRows((p) => fillEmptyRows(p, add));
            api.post(`/projects/${projectId}/ai-analyses/${analysis.id}/imported`, { links: add.map((r) => ({ row_id: r.id, idx: r.ai_src.idx })) }).catch(() => {});
        }
        toast.success(t("ah_restored"));
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent data-testid="ai-history-dialog" className="max-w-[96vw] w-[96vw] bg-[#111827] border-[#1e293b] text-slate-100">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2 font-mono"><History className="w-4 h-4 text-fuchsia-400" /> {t("ah_title")}</DialogTitle>
                    <DialogDescription className="text-slate-400">{t("ah_desc")}</DialogDescription>
                </DialogHeader>
                {list === null ? <Loader2 className="w-6 h-6 animate-spin text-blue-400 mx-auto" /> : !list.length ? (
                    <div data-testid="ah-empty" className="py-10 text-center text-slate-400">{t("ah_empty")}</div>
                ) : (
                    <Tabs defaultValue="compare">
                        <div className="flex items-center gap-2 flex-wrap">
                            <TabsList className="bg-[#0b0f17]">
                                <TabsTrigger data-testid="ah-tab-compare" value="compare">{t("ah_tab_compare")}</TabsTrigger>
                                <TabsTrigger data-testid="ah-tab-archive" value="archive">{t("ah_tab_archive")}</TabsTrigger>
                            </TabsList>
                            <select data-testid="ah-select" value={cur || ""} onChange={(e) => setCur(e.target.value)} className="h-9 bg-[#0b0f17] border border-[#1e293b] rounded px-2 text-sm">
                                {list.map((a) => <option key={a.id} value={a.id}>{fmtDate(a.created_at)} · {a.user_name} · {a.pdf_filename || "PDF"} ({a.item_count})</option>)}
                            </select>
                            <Button data-testid="ah-download-pdf" size="sm" variant="outline" onClick={downloadVersion} className="bg-transparent border-[#1e293b] text-slate-200 hover:bg-[#182232]"><FileDown className="w-4 h-4 mr-1" /> {t("ah_pdf")}</Button>
                            {analysis && <Button data-testid="ah-open-review" size="sm" variant="outline" onClick={() => { onOpenChange(false); onOpenReview(analysis); }} className="bg-transparent border-fuchsia-500/50 text-fuchsia-200 hover:bg-fuchsia-500/10">{t("ah_open_review")}</Button>}
                        </div>
                        <TabsContent value="compare" className="mt-3">
                            {analysis ? <AiCompareTable analysis={analysis} rows={rows} setRows={setRows} isEmptyRow={isEmptyRow} onRestore={restoreAll} t={t} /> : <Loader2 className="w-5 h-5 animate-spin text-blue-400" />}
                        </TabsContent>
                        <TabsContent value="archive" className="mt-3">
                            <AiArchive list={list} load={load} t={t} />
                        </TabsContent>
                    </Tabs>
                )}
            </DialogContent>
        </Dialog>
    );
}
